import merge from "lodash/merge";
import { ChartCandle, HistogramData, HistogramSeriesOptions } from "../chart.typings";
import BaseChartSeries from "./baseSeries";
import { histogramSeriesOptions } from "../../../assets/constants";
import { DeepPartial } from "lightweight-charts";
import { ChartType } from "../../../proto/generated/chart";
import { evaluateExpression } from "../../../util/safeExpressionEval";
import { SeriesGroupCategory } from "../../../pages/home";

class HistogramSeries extends BaseChartSeries<HistogramData, HistogramSeriesOptions> {

    addSeriesToChart() {
        return this.chart.chartApi.addHistogramSeries(this.seriesOptions)
    }

    getDefaultOptions(): HistogramSeriesOptions {
        const {thickness, chartType} = this.config;
        const options: HistogramSeriesOptions = {
            source: "close",
            columnWidth: thickness || 1,
            columns: chartType === ChartType.HISTOGRAM
        };
        return merge({}, histogramSeriesOptions, options, this.options)
    }
    onOptionsUpdate(options: DeepPartial<HistogramSeriesOptions>): void {
            this._setData(this.rawData, this.metadata)
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}): HistogramData[] {
        const result: HistogramData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);
        const referenceSeries: number[] = new Array(data.length);

        const { color: seriesColor } = this.seriesApi.options();
        const { color: colorExpr, label: labelExpr } = metadata;
        // The volume indicator keeps its default up/down fills, so its config color is ignored.
        const isVolume = this.seriesGroup?.category === SeriesGroupCategory.SERIES_VOLUME;
        const configColor = isVolume ? undefined : this.config.color;
        // A plain hex from the script is a default; the user-selected config color overrides it.
        const isColorHexCode = colorExpr && RegExp(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/).test(colorExpr);
        const staticColor = configColor || (isColorHexCode ? colorExpr : undefined);
        const source = this.seriesOptions.source;

        // Keep the series base color in sync with the resolved static color.
        if (staticColor && seriesColor !== staticColor) {
            this.seriesApi.applyOptions({ color: staticColor });
        }

        let valuePresent = false;

        data.forEach((candle, i) => {
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0;
            referenceSeries[i] = candle.referenceSeries || 0;

            const val = candle[source];
            if (!val) {
                if (!valuePresent) return;
                result.push({
                    ...candle,
                    value: Number.NaN,
                    color: "transparent",
                    label: ""
                });
                return;
            }

            valuePresent = true;

            const upColor = this.seriesOptions.upColor || "#26a69a80";
            const downColor = this.seriesOptions.downColor || "#ef535080";

            let candleColor: string;
            if (isColorHexCode) {
                candleColor = staticColor as string;
            } else if (colorExpr) {
                try {
                    candleColor = evaluateExpression<string>(colorExpr, { open, close, series, referenceSeries, candle, i });
                } catch (e) {
                    console.warn("Invalid color expression:", colorExpr);
                    candleColor = configColor || (candle.close > candle.open ? upColor : downColor);
                }
            } else {
                candleColor = configColor || (candle.close > candle.open ? upColor : downColor);
            }

            result.push({
                ...candle,
                value: val as number,
                color: candleColor,
                label: labelExpr ? String(evaluateExpression(labelExpr, { open, close, series, referenceSeries, candle, i })) : ''
            });
        });

        return result;
    }


}

export default HistogramSeries;